import { resolve } from '@bonfida/spl-name-service'
import { Connection } from '@solana/web3.js'

import { connection } from '@src/chains/solana/anchor/setup'
import { logger } from '@src/utils/logger'

/**
 * Checks if a string is a valid SNS domain (.sol)
 * @param domain The domain to check
 * @returns True if the domain is a valid SNS domain
 */
export const isSNSDomain = (domain: string): boolean => {
    if (!domain) return false
    return domain.toLowerCase().endsWith('.sol')
}

/**
 * Resolves an SNS domain to a Solana address
 * @param domain The SNS domain to resolve (e.g., "example.sol")
 * @returns The resolved Solana address as string, or null if resolution fails
 */
export const resolveSNSDomain = async (domain: string, conn: Connection = connection): Promise<string | null> => {
    try {
        // Make sure the domain has the .sol suffix
        const normalizedDomain = domain.toLowerCase().endsWith('.sol')
            ? domain.toLowerCase()
            : `${domain.toLowerCase()}.sol`

        // Resolve the domain using the SNS library
        const publicKey = await resolve(conn, normalizedDomain)

        // Return the resolved public key as a string
        return publicKey.toString()
    } catch (error) {
        logger.error(`Failed to resolve SNS domain ${domain}:`, error)
        return null
    }
}

/**
 * Checks if an SNS domain is valid and resolvable
 * @param domain The SNS domain to validate
 * @returns True if the domain is valid and resolvable
 */
export const validateSNSDomain = async (domain: string, conn: Connection = connection): Promise<boolean> => {
    try {
        const address = await resolveSNSDomain(domain, conn)
        return address !== null
    } catch (error) {
        return false
    }
}
